import React, { useEffect, useRef, useState } from 'react';

const RETAKE_LABELS = {
  same: '↺ Retake (same questions)',
  new: '↺ Retake (new questions)',
  mixed: '↺ Retake (mixed)',
};

const styles = `
.tcm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.tcm-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.tcm-card {
  position: relative;
  z-index: 1;
  background: white;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  max-width: 380px;
  width: 90%;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.tcm-emoji {
  font-size: 48px;
  margin-bottom: 1rem;
}

.tcm-title {
  font-size: 22px;
  font-weight: 500;
  margin: 0 0 0.25rem;
  color: #1a1a1a;
}

.tcm-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0 0 1.5rem;
}

.tcm-score-box {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.tcm-score-label {
  font-size: 13px;
  color: #666;
  margin: 0 0 0.25rem;
}

.tcm-score {
  font-size: 40px;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0;
}

.tcm-score-denom {
  font-size: 20px;
  color: #999;
}

.tcm-actions {
  display: flex;
  gap: 10px;
}

.tcm-btn {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: white;
  color: #1a1a1a;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s;
}

.tcm-btn:hover {
  background: #f5f5f5;
}

.tcm-btn--retake {
  background: rgba(60, 200, 120, 0.14);
  border-color: rgba(40, 170, 90, 0.45);
  color: #2a7a50;
}

.tcm-btn--retake:hover {
  background: rgba(60, 200, 120, 0.3);
}

.tcm-breakdown-toggle {
  width: 100%;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #555;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 10px;
  transition: background 0.15s;
}

.tcm-breakdown-toggle:hover { background: #f5f5f5; }

.tcm-breakdown {
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  text-align: left;
}

.tcm-q-card {
  background: #f9fafb;
  border-radius: 8px;
  padding: 8px 10px;
  border-left: 3px solid #d1d5db;
}

.tcm-q-card--good { border-left-color: #1D9E75; }
.tcm-q-card--mid  { border-left-color: #EF9F27; }
.tcm-q-card--low  { border-left-color: #E24B4A; }

.tcm-q-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3px;
}

.tcm-q-num { font-size: 12px; font-weight: 600; color: #555; }

.tcm-q-grade {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 99px;
  background: #e5e7eb;
  color: #374151;
}

.tcm-q-grade--good { background: #d1fae5; color: #065f46; }
.tcm-q-grade--mid  { background: #fef3c7; color: #92400e; }
.tcm-q-grade--low  { background: #fee2e2; color: #991b1b; }

.tcm-q-answer {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
`;

function TestCompleteModal({ isOpen, score, title, subtitle, questionResults, totalQuestions, onClose, onRestart, onRetake, retakePolicy }) {
  title = title || 'Test complete!';
  subtitle = subtitle || 'Great work finishing this session.';
  const [showBreakdown, setShowBreakdown] = useState(false);
  const displayResults = totalQuestions > 0
    ? (questionResults || []).slice(0, totalQuestions)
    : (questionResults || []);
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    if (!document.getElementById('tcm-styles')) {
      const tag = document.createElement('style');
      tag.id = 'tcm-styles';
      tag.textContent = styles;
      document.head.appendChild(tag);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const canvas = canvasRef.current;
    const overlay = canvas?.parentElement;
    if (!canvas || !overlay) return;

    const ctx = canvas.getContext('2d');

    canvas.width = overlay.offsetWidth;
    canvas.height = overlay.offsetHeight;

    const colors = [
      '#7F77DD', '#1D9E75', '#D85A30', '#D4537E',
      '#378ADD', '#EF9F27', '#E24B4A', '#5DCAA5'
    ];

    const pieces = Array.from({ length: 130 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * -canvas.height,
      w: 7 + Math.random() * 7,
      h: 10 + Math.random() * 6,
      color: colors[Math.floor(Math.random() * colors.length)],
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.12,
      vx: (Math.random() - 0.5) * 2.5,
      vy: 2.5 + Math.random() * 3,
      opacity: 1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let allDone = true;

      for (const p of pieces) {
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.rotSpeed;
        if (p.y > canvas.height * 0.7) {
          p.opacity = Math.max(0, p.opacity - 0.025);
        }
        if (p.opacity > 0) allDone = false;

        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      if (!allDone) {
        animRef.current = requestAnimationFrame(draw);
      }
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="tcm-overlay">
      <canvas ref={canvasRef} className="tcm-canvas" />
      <div className="tcm-card">
        <div className="tcm-emoji">🎉</div>
        <h2 className="tcm-title">{title}</h2>
        <p className="tcm-subtitle">{subtitle}</p>

        {score != null && (
          <div className="tcm-score-box">
            <p className="tcm-score-label">Average score</p>
            <p className="tcm-score">
              {score}
              <span className="tcm-score-denom">/100</span>
            </p>
          </div>
        )}

        {displayResults.length > 0 && (
          <>
            <button className="tcm-breakdown-toggle" onClick={() => setShowBreakdown(v => !v)}>
              {showBreakdown ? '▲ Hide breakdown' : '▼ View question breakdown'}
            </button>
            {showBreakdown && (
              <div className="tcm-breakdown">
                {displayResults.map((q, i) => {
                  const grade = q.numerical_grade ?? 0;
                  const tier = grade >= 80 ? 'good' : grade >= 60 ? 'mid' : 'low';
                  return (
                    <div key={i} className={`tcm-q-card tcm-q-card--${tier}`}>
                      <div className="tcm-q-header">
                        <span className="tcm-q-num">Q{q.question_number}</span>
                        <span className={`tcm-q-grade tcm-q-grade--${tier}`}>
                          {q.categorical_grade || `${grade}%`}
                        </span>
                      </div>
                      {q.student_response && (
                        <p className="tcm-q-answer">
                          {q.student_response.length > 160
                            ? q.student_response.slice(0, 160) + '…'
                            : q.student_response}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div className="tcm-actions">
          <button className="tcm-btn" onClick={onClose}>
            ✕ Close
          </button>
          {onRetake ? (
            <button className="tcm-btn tcm-btn--retake" onClick={onRetake}>
              {RETAKE_LABELS[retakePolicy] || '↺ Retake'}
            </button>
          ) : onRestart ? (
            <button className="tcm-btn" onClick={onRestart}>
              ↺ Restart
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default TestCompleteModal;